import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import FormField from "../ui/FormField";
import { Button } from "../ui/Button";
import { getSunbedRooms, SunbedMembershipPlanApi } from "../../services/sunbeds";

export interface MembershipPlanFormInput {
  id?: number;
  name: string;
  description?: string;
  status: "active" | "inactive";
  interval: "week" | "month" | "year";
  interval_count: number;
  price: number;
  entitlement: "unlimited" | "minutes";
  minutes_per_period?: number | null;
  rollover: boolean;
  max_session_minutes?: number | null;
  room_ids: number[];
}

interface AddMembershipPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: MembershipPlanFormInput) => Promise<void>;
  existingPlan?: SunbedMembershipPlanApi | null;
  mode: "add" | "edit";
}

export const AddMembershipPlanModal = ({
  isOpen,
  onClose,
  onSave,
  existingPlan,
  mode,
}: AddMembershipPlanModalProps) => {
  const [rooms, setRooms] = useState<Array<{ id: number | string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [intervalUnit, setIntervalUnit] = useState<"week" | "month" | "year">("month");
  const [intervalCount, setIntervalCount] = useState("1");
  const [price, setPrice] = useState("");
  const [entitlement, setEntitlement] = useState<"unlimited" | "minutes">("minutes");
  const [minutesPerPeriod, setMinutesPerPeriod] = useState("");
  const [rollover, setRollover] = useState(false);
  const [maxSessionMinutes, setMaxSessionMinutes] = useState("");
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSunbedRooms();
      const data = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setRooms(data);
    } catch (error) {
      console.error("Failed to load rooms", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void fetchRooms();
    if (existingPlan) {
      setName(existingPlan.name || "");
      setDescription(existingPlan.description || "");
      setStatus(existingPlan.status);
      setIntervalUnit(existingPlan.interval);
      setIntervalCount(String(existingPlan.interval_count || 1));
      setPrice(String(existingPlan.price ?? ""));
      setEntitlement(existingPlan.entitlement);
      setMinutesPerPeriod(existingPlan.minutes_per_period ? String(existingPlan.minutes_per_period) : "");
      setRollover(Boolean(existingPlan.rollover));
      setMaxSessionMinutes(existingPlan.max_session_minutes ? String(existingPlan.max_session_minutes) : "");
      setSelectedRoomIds((existingPlan.rooms || []).map((room) => Number(room.id)));
    } else {
      setName("");
      setDescription("");
      setStatus("active");
      setIntervalUnit("month");
      setIntervalCount("1");
      setPrice("");
      setEntitlement("minutes");
      setMinutesPerPeriod("");
      setRollover(false);
      setMaxSessionMinutes("");
      setSelectedRoomIds([]);
    }
    setFormError(null);
  }, [isOpen, existingPlan, fetchRooms]);

  const toggleRoom = (id: number) => {
    setSelectedRoomIds((prev) =>
      prev.includes(id) ? prev.filter((roomId) => roomId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError("Plan name is required.");
      return;
    }
    const parsedPrice = Number(price);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setFormError("Enter a valid price.");
      return;
    }
    const parsedIntervalCount = Number(intervalCount) || 1;
    if (entitlement === "minutes" && (!Number(minutesPerPeriod) || Number(minutesPerPeriod) < 1)) {
      setFormError("Minutes per period is required for minute-based plans.");
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        status,
        interval: intervalUnit,
        interval_count: parsedIntervalCount,
        price: parsedPrice,
        entitlement,
        minutes_per_period: entitlement === "minutes" ? Number(minutesPerPeriod) : null,
        rollover: entitlement === "minutes" ? rollover : false,
        max_session_minutes: maxSessionMinutes ? Number(maxSessionMinutes) : null,
        room_ids: selectedRoomIds,
      });
      onClose();
    } catch {
      // Parent shows the error
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#885ABB] flex justify-between items-center bg-[#885ABB] rounded-t-xl">
          <h2 className="text-xl font-bold text-white">
            {mode === "add" ? "Add membership plan" : "Edit membership plan"}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Name *"
              name="name"
              value={name}
              onChange={(_, val) => setName(val)}
              placeholder="Unlimited monthly"
            />
            <FormField
              label="Status"
              name="status"
              type="select"
              value={status}
              onChange={(_, val) => setStatus(val as "active" | "inactive")}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </div>

          <FormField
            label="Description"
            name="description"
            value={description}
            onChange={(_, val) => setDescription(val)}
            placeholder="What this plan includes"
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField
              label="Billing interval"
              name="interval"
              type="select"
              value={intervalUnit}
              onChange={(_, val) => setIntervalUnit(val as "week" | "month" | "year")}
              options={[
                { value: "week", label: "Weekly" },
                { value: "month", label: "Monthly" },
                { value: "year", label: "Yearly" },
              ]}
            />
            <FormField
              label="Every"
              name="interval_count"
              type="number"
              value={intervalCount}
              onChange={(_, val) => setIntervalCount(val)}
              placeholder="1"
            />
            <FormField
              label="Price *"
              name="price"
              value={price}
              onChange={(_, val) => setPrice(val)}
              placeholder="29.99"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-[#402B69]">Entitlement</p>
            <div className="flex flex-wrap gap-2">
              {(["minutes", "unlimited"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setEntitlement(option)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm border",
                    entitlement === option
                      ? "bg-[#885ABB] text-white border-[#885ABB]"
                      : "bg-white text-gray-700 border-gray-200"
                  )}
                >
                  {option === "unlimited" ? "Unlimited" : "Minutes per period"}
                </button>
              ))}
            </div>
          </div>

          {entitlement === "minutes" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Minutes per period *"
                name="minutes"
                type="number"
                value={minutesPerPeriod}
                onChange={(_, val) => setMinutesPerPeriod(val)}
                placeholder="60"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700 pt-7">
                <input
                  type="checkbox"
                  checked={rollover}
                  onChange={(e) => setRollover(e.target.checked)}
                />
                Unused minutes roll over
              </label>
            </div>
          )}

          <FormField
            label="Max minutes per session (optional)"
            name="max_session"
            type="number"
            value={maxSessionMinutes}
            onChange={(_, val) => setMaxSessionMinutes(val)}
            placeholder="Leave blank for no cap"
          />

          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#402B69]">
              Rooms
            </label>
            <p className="text-xs text-gray-500">Leave empty to allow every sunbed room.</p>
            <div className="min-h-[50px] rounded-lg border border-gray-200 bg-[#F9F9F9] px-4 py-3">
              {loading ? (
                <p className="text-sm text-gray-500">Loading rooms...</p>
              ) : rooms.length === 0 ? (
                <p className="text-sm text-amber-700">No rooms found.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      type="button"
                      onClick={() => toggleRoom(Number(room.id))}
                      className={cn(
                        "rounded-full px-3 py-1 text-sm border",
                        selectedRoomIds.includes(Number(room.id))
                          ? "bg-[#885ABB] text-white border-[#885ABB]"
                          : "bg-white text-gray-700 border-gray-200"
                      )}
                    >
                      {room.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white"
            >
              {isSaving ? "Saving..." : mode === "add" ? "Create plan" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
